/**
 * Keeps a signed-in session signed in.
 *
 * The dashboard's access token lives 30 minutes; the refresh chain behind
 * it rolls for a year. Nothing used to bridge the two proactively — the
 * app just waited for something to 401 and refreshed reactively. That is
 * exactly the wrong moment to need the network: the 401 storm lands the
 * instant a laptop wakes, when the wifi often isn't back yet, and a
 * refresh that fails then used to end the session (see docs/session-lifetime.md).
 *
 * So: refresh a few minutes BEFORE expiry, and again whenever the app
 * comes back to life (tab visible / window focused / browser reports
 * online) with an access token that is expired or nearly so. Transient
 * failures retry with backoff — they never end the session; only a
 * server-rejected refresh token does, and that path is owned by
 * `notifySessionLost` in lib/api.ts.
 */

import { refreshSessionTokens, type RefreshOutcome } from "./api.js";
import { tokenStore } from "./tokenStore.js";

/** Refresh this long before the access token expires. */
export const RENEW_LEAD_MS = 5 * 60 * 1000;
/** Never schedule a timer tighter than this (avoids a hot loop on a token
 * that is already expired or has a nonsense exp). */
const MIN_DELAY_MS = 15 * 1000;
/** Backoff ladder for transient failures (offline, 5xx, server restarting). */
const RETRY_BACKOFF_MS = [15_000, 30_000, 60_000, 120_000, 300_000];

/**
 * Read the `exp` of a JWT without verifying it. Purely for scheduling —
 * the signature is the server's business; a wrong guess here costs one
 * unnecessary (or slightly late) refresh, nothing more.
 */
export function accessTokenExpiryMs(token: string | null): number | null {
  if (!token) return null;
  const payload = token.split(".")[1];
  if (!payload) return null;
  try {
    const padded = payload.padEnd(payload.length + ((4 - (payload.length % 4)) % 4), "=");
    const json = atob(padded.replace(/-/g, "+").replace(/_/g, "/"));
    const claims = JSON.parse(json) as { exp?: number };
    return typeof claims.exp === "number" ? claims.exp * 1000 : null;
  } catch {
    return null;
  }
}

export interface KeepAliveOptions {
  /** Injected in tests. */
  refresh?: () => Promise<RefreshOutcome>;
  now?: () => number;
  /** Called when the session is definitively over (the server rejected the
   * refresh token). lib/api.ts already fires the global session-lost event
   * in that case, so the default is a no-op. */
  onDead?: () => void;
}

/**
 * Start the keep-alive loop. Returns a `stop()` for the caller's effect
 * cleanup. Safe to call when there is no session — it simply idles until
 * tokens appear.
 */
export function startSessionKeepAlive(opts: KeepAliveOptions = {}): () => void {
  const refresh = opts.refresh ?? refreshSessionTokens;
  const now = opts.now ?? (() => Date.now());
  const onDead = opts.onDead ?? ((): void => {});

  let timer: ReturnType<typeof setTimeout> | null = null;
  let retryIndex = 0;
  let stopped = false;
  let running = false;

  function clearTimer(): void {
    if (timer !== null) {
      clearTimeout(timer);
      timer = null;
    }
  }

  function schedule(delayMs: number): void {
    if (stopped) return;
    clearTimer();
    timer = setTimeout(() => {
      timer = null;
      void tick();
    }, Math.max(MIN_DELAY_MS, delayMs));
  }

  /** Re-arm from the CURRENT access token, or idle if there is no session. */
  function rearm(): void {
    if (stopped) return;
    const exp = accessTokenExpiryMs(tokenStore.getAccess());
    if (exp === null) {
      // No session (or an unreadable token) — poll lazily; login writes
      // tokens without telling us, and this picks them up.
      schedule(RETRY_BACKOFF_MS[0]!);
      return;
    }
    schedule(exp - RENEW_LEAD_MS - now());
  }

  async function tick(): Promise<void> {
    if (stopped || running) return;
    if (!tokenStore.getRefresh()) {
      rearm();
      return;
    }
    running = true;
    try {
      const outcome = await refresh();
      if (stopped) return;
      if (outcome === "ok") {
        retryIndex = 0;
        rearm();
      } else if (outcome === "transient") {
        const wait = RETRY_BACKOFF_MS[Math.min(retryIndex, RETRY_BACKOFF_MS.length - 1)]!;
        retryIndex += 1;
        schedule(wait);
      } else {
        clearTimer(); // dead — lib/api.ts has already signalled session-lost
        onDead();
      }
    } finally {
      running = false;
    }
  }

  /** The app just came back (tab visible / focused / online). Refresh now
   * if the token is expired or about to be; otherwise just re-arm, because
   * timers are unreliable across sleep. */
  function onWake(): void {
    if (stopped) return;
    const exp = accessTokenExpiryMs(tokenStore.getAccess());
    if (exp !== null && exp - now() > RENEW_LEAD_MS) {
      rearm();
      return;
    }
    retryIndex = 0;
    void tick();
  }

  const onVisible = (): void => {
    if (document.visibilityState === "visible") onWake();
  };

  if (typeof window !== "undefined") {
    window.addEventListener("online", onWake);
    window.addEventListener("focus", onWake);
    document.addEventListener("visibilitychange", onVisible);
  }
  rearm();

  return () => {
    stopped = true;
    clearTimer();
    if (typeof window !== "undefined") {
      window.removeEventListener("online", onWake);
      window.removeEventListener("focus", onWake);
      document.removeEventListener("visibilitychange", onVisible);
    }
  };
}
